import { useState } from 'react';
import { useFieldArray, type Control, type FieldErrors } from 'react-hook-form';
import { Button, Chip, LoadingButton, TextInput } from '@zarya/ui';
import { t, tn } from '@app/i18n';
import type { KindResource } from './Resources.api';
import { toolDefaults, type ResourceFormValues, type ToolFormValues } from './ResourceModel';
import { ToolRow } from './ToolRow';
import s from './Resources.module.css';

/**
 * Aba **Tools** do drawer T7 (FR-030, FR-032, FR-034).
 *
 * A nota do topo repete o indicador de T6 ("{n} tools · {k} sem risco") porque é
 * aqui que ele se resolve: classificar o risco é o trabalho desta aba.
 * **Reimportar do gateway** só existe para resources importados; **Adicionar
 * linha**, só onde a tool pode nascer aqui (`declaravel`).
 */

export interface ToolsTabProps {
  control: Control<ResourceFormValues>;
  errors: FieldErrors<ResourceFormValues>;
  /** Valores atuais das tools (observados do formulário). */
  tools: ToolFormValues[];
  /** Identificador no gateway — prefixo da capability (`<apim_id>/<tool>`). */
  apimId: string;
  tipo: KindResource;
  manual: boolean;
  importado: boolean;
  /** Governado pela policy global (aim-uig.40 §5.1): um MCP governado lista as tools pelo gateway. */
  governed: boolean;
  /**
   * A tool pode ser criada nesta tela: só no recurso manual (FR-027). Num importado as tools
   * vêm do gateway; o MCP por policy antigo, com tools declaradas aqui, saiu (aim-cpj).
   */
  declaravel: boolean;
  editavel: boolean;
  reimportando?: boolean;
  onReimportar?: () => void;
  /** Tool já gravada: remover passa pela confirmação de impacto (FR-035). */
  onRemoverGravada: (toolId: string) => void;
}

/** O motivo do vazio da aba e a chave literal de cada um (as chaves ficam por extenso para a busca). */
const EMPTY_KEY = {
  governedMcp: 'resources.toolsTab.empty.governedMcp',
  declarable: 'resources.toolsTab.empty.declarable',
  notDeclarable: 'resources.toolsTab.empty.notDeclarable',
} as const;

export function ToolsTab({
  control,
  errors,
  tools,
  apimId,
  tipo,
  manual,
  importado,
  governed,
  declaravel,
  editavel,
  reimportando = false,
  onReimportar,
  onRemoverGravada,
}: ToolsTabProps) {
  const { fields, append, remove } = useFieldArray({ control, name: 'tools' });
  const [filter, setFiltro] = useState('');
  const [editando, setEditando] = useState<number | null>(null);

  const withoutRisk = tools.filter((tool) => tool.risk === '').length;
  /**
   * O vazio diz onde a tool nasce: no recurso manual, nesta tela; num importado, no gateway.
   *
   * aim-4dn (D1, D2): um servidor MCP governado — por policy ou nativo — tem as tools do
   * `tools/list` pelo gateway, e o vazio diz isso nas duas formas. `emptyKind` vai também
   * no `data-empty` do parágrafo: é a estrutura que os testes leem.
   */
  const emptyKind =
    governed && tipo === 'mcp' ? 'governedMcp' : declaravel ? 'declarable' : 'notDeclarable';
  const empty = t(EMPTY_KEY[emptyKind]);
  /** `<apim_id>/<tool>` — o texto que o contrato grava em `capability`. */
  const capabilities = tools
    .filter((tool) => tool.apim_name.trim())
    .map(
      (tool) =>
        `${apimId || t('resources.toolsTab.identifierPlaceholder')}/${tool.apim_name.trim()}`,
    );
  const search = filter.trim().toLowerCase();
  const visivel = (tool: ToolFormValues) =>
    !search || `${tool.apim_name} ${tool.path}`.toLowerCase().includes(search);

  return (
    <div className={s.toolsAba}>
      <div className={s.toolsBarra}>
        <TextInput
          type="search"
          value={filter}
          aria-label={t('resources.toolsTab.filterPlaceholder')}
          placeholder={t('resources.toolsTab.filterPlaceholder')}
          onChange={(event) => setFiltro(event.target.value)}
        />
        <span className={s.toolsAcoes}>
          {editavel && importado && onReimportar ? (
            <LoadingButton
              variant="out"
              icon="refresh"
              loading={reimportando}
              loadingLabel={t('resources.toolsTab.reimport.loading')}
              onClick={onReimportar}
            >
              {t('resources.toolsTab.reimport')}
            </LoadingButton>
          ) : null}
          {editavel && declaravel ? (
            <Button
              variant="out"
              icon="plus"
              onClick={() => {
                append(toolDefaults());
                setEditando(fields.length);
              }}
            >
              {t('resources.toolsTab.addRow')}
            </Button>
          ) : null}
        </span>
        <span className={s.toolsNota}>
          {t('resources.toolsTab.note', {
            tools: tn('resources.toolsTab.count', tools.length),
            withoutRisk,
          })}
        </span>
      </div>

      {typeof errors.tools?.message === 'string' ? (
        <p className={s.erroLinha}>{errors.tools.message}</p>
      ) : null}

      {fields.length === 0 ? (
        <p className={s.vazioTexto} data-testid="tools-empty" data-empty={emptyKind}>
          {empty}
        </p>
      ) : (
        <ul className={s.tools_lista}>
          {fields.map((campo, index) => {
            const tool = tools[index];
            if (!tool || !visivel(tool)) return null;
            return (
              <ToolRow
                key={campo.id}
                index={index}
                tool={tool}
                control={control}
                errors={errors.tools?.[index]}
                tipo={tipo}
                manual={manual}
                importada={importado && tool.id !== null}
                editavel={editavel}
                editando={editando === index}
                onEditar={() => setEditando(index)}
                onConcluir={() => setEditando(null)}
                onRemover={() => {
                  // Tool nunca gravada sai do rascunho; gravada passa pela
                  // confirmação de impacto e pela rota de exclusão (FR-035).
                  if (tool.id === null) {
                    remove(index);
                    setEditando(null);
                  } else {
                    onRemoverGravada(tool.id);
                  }
                }}
              />
            );
          })}
        </ul>
      )}

      {/*
        "Capabilities resultantes" do protótipo (§ aba Tools), com o formato que
        este produto usa de verdade: `<apim_id>/<tool>` — o mesmo texto que o
        contrato grava em `capability` e que o diff das aprovações exibe. É aqui
        que se vê o efeito de renomear uma tool de recurso manual.
      */}
      {capabilities.length ? (
        <p className={s.capsNota} data-testid="capabilities-resultantes">
          {t('resources.toolsTab.capabilities')}{' '}
          {capabilities.slice(0, 6).map((capability) => (
            <Chip key={capability}>{capability}</Chip>
          ))}
          {capabilities.length > 6 ? (
            <span className={s.sub}>+{capabilities.length - 6}</span>
          ) : null}
        </p>
      ) : null}
    </div>
  );
}
